import type { QDialogOptions } from 'quasar'
import type { LoginForm, User } from 'src/types'
import * as Sentry from '@sentry/vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { Cookies, Dark, Dialog, Loading, Notify } from 'quasar'

const COOKIE_NAME = 'XSRF-TOKEN'

interface State {
  loggedIn: boolean
  user: Partial<User> // & { supabase: { url: string, key: string } }
}

async function promisifiedDialog(dialogOptions: QDialogOptions) {
  return new Promise((resolve, reject) => {
    Dialog.create(dialogOptions)
      .onOk(successResponse => { resolve(successResponse) })
      .onCancel(() => reject())
  })
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    loggedIn: false,
    user: {
      label: '',
      two_factor_secret: '',
      // supabase: {
      //   url: '',
      //   key: '',
      // },
    },
  }),

  // getters: {
  //   twoFactorEnabled: state => Boolean(state.user.two_factor_secret),
  // },

  actions: {
    async init() {
      try {
        Loading.show()
        // if (!Cookies.has(COOKIE_NAME)) throw new Error('Cookie non presente')
        await this.getUser()
        this.$patch({ loggedIn: true })
      } catch {
        this.$patch({ loggedIn: false })
      } finally { Loading.hide() }
    },

    async login(form: LoginForm) {
      !Cookies.has(COOKIE_NAME) && await api.get('../sanctum/csrf-cookie')
      const { data: { two_factor } } = await api.post('login', form)
      if (two_factor) {
        const code = await promisifiedDialog({
          title: 'Autenticazione a due fattori',
          cancel: true,
          persistent: true,
          prompt: {
            model: '',
            // mask: '######',
            maxlength: 6,
            isValid: value => value.length === 6,
          },
        })
        await api.post('two-factor-challenge', { code })
      }
      await this.init()
    },

    async logout() {
      try {
        Loading.show()
        await api.post('logout')
        Sentry.setUser(null)
        this.$reset()
      } catch (error) {
        Notify.create({ type: 'negative', message: 'Qualcosa è andato storto.' })
        throw error
      } finally { Loading.hide() }
    },

    async getUser() {
      const isPushSupported = 'serviceWorker' in navigator && 'PushManager' in window
      const isDenoSupported = await deno.get('/').then(() => true).catch(() => false)
      const { data } = await api.get('user', { params: { isDenoSupported, isPushSupported } })
      Sentry.setUser({
        id: `${data.id}`,
        username: data.label,
        email: data.email,
      })
      this.$patch({ user: data })
    },

    async checkIfPasswordIsRequired() {
      const { data: { confirmed } } = await api.get('user/confirmed-password-status')
      if (!confirmed) {
        const password = await promisifiedDialog({
          title: 'Conferma Password',
          message: 'Per questioni di sicurezza inserisci la password corrente',
          cancel: true,
          persistent: true,
          prompt: { model: '', type: 'password' },
        })
        await api.post('user/confirm-password', { password })
      }
    },

    async enableTwoFactor() {
      await this.checkIfPasswordIsRequired()
      await api.post('user/two-factor-authentication')
      try {
        const svg = await this.getQrCode()
        const code = await promisifiedDialog({
          title: 'Inquadra il Codice QR',
          message: `<div class="flex justify-center"><div style="padding: 6px 6px 0px 6px;width:fit-content;background-color:white;">${svg}</div></div>`,
          prompt: {
            model: '',
            // mask: '######',
            maxlength: 6,
            isValid: value => value.length === 6,
          },
          html: true,
          dark: Dark.isActive,
          cancel: true,
          ok: 'Conferma',
        })
        await api.post('user/confirmed-two-factor-authentication', { code })
        await this.getUser()
        Notify.create({ type: 'positive', message: 'Autenticazione a due fattori abilitata' })
      } catch (error) {
        error && Notify.create({ type: 'negative', message: 'Codice OTP non valido' })
        await this.disableTwoFactor()
      }
    },

    async disableTwoFactor() {
      await this.checkIfPasswordIsRequired()
      await api.delete('user/two-factor-authentication')
      this.$patch({ user: { ...this.user, two_factor_secret: '' } })
    },

    async getQrCode() {
      await this.checkIfPasswordIsRequired()
      const { data: { svg } } = await api.get('user/two-factor-qr-code')
      return svg
    },

    async generateRecoveryCodes() {
      await this.checkIfPasswordIsRequired()
      const { data } = await api.get<string[]>('user/two-factor-recovery-codes')
      const message = `<ul>${data.map(code => `<li>${code}</li>`).join('')}</ul>`
      Dialog.create({
        title: 'Codici di Recupero',
        message,
        html: true,
        ok: 'Chiudi',
      })
    },

    // NON USATI
    // async sendMail () {
    //   await api.post('email/verification-notification')
    //   Notify.create({ type: 'positive', message: 'Email Inviata' })
    // },

    // async updatePassword (passwords) {
    //   await api.put('user/password', passwords)
    // },

    // forgotPassword (model: string) {
    //   Dialog.create({
    //     title: 'Recupera Password',
    //     message: 'Inserisci l\'indirizzo email con cui hai effettuato la registrazione',
    //     cancel: true,
    //     persistent: true,
    //     prompt: { model, type: 'email', isValid: Boolean },
    //   }).onOk(async email => {
    //     Loading.show()
    //     await api.post('forgot-password', { email })
    //     Loading.hide()
    //     Notify.create({ type: 'positive', message: 'Email Inviata con successo', caption: 'Segui le istruzioni all\'interno della mail per resettare la password' })
    //   })
    // },

    // async resetPassword (form) {
    //   await api.post('reset-password', form)
    //   Notify.create({ type: 'positive', message: 'Password aggiornata' })
    // },
  },
})

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
